import { onlineManager } from '@tanstack/react-query';
import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import storage from '../../../../hooks/useStorage/useStorage';
import useUserOnlineStatus from '../../../../hooks/useUserOnlineStatus';
import api from '../../../../services/api';
import { NetworkContextValues, SetIsForcingOfflineModeParams } from './network.context.types';

const NetworkContext = createContext<NetworkContextValues>({
  isOnline: true,
  userHasInternet: true,
  isLoadingOnlineStatus: true,
  isForcingOfflineMode: false,
  setIsForcingOfflineMode: () => null,
});

export const useNetworkContext = () => useContext(NetworkContext);

export const NetworkContextProvider = ({ children }: PropsWithChildren) => {
  const history = useHistory();

  const { isOnline: realIsOnlineState, isLoadingOnlineStatus } = useUserOnlineStatus();

  const [isForcingOfflineMode, setIsForcingOfflineMode] = useState<boolean>(
    storage.getForceOffline(),
  );

  const handleSetIsForcingOfflineMode = ({ forceOfflineMode }: SetIsForcingOfflineModeParams) => {
    storage.setForceOffline(forceOfflineMode);
    setTimeout(setIsForcingOfflineMode, 1000, forceOfflineMode);

    history.push('/');
    window.location.reload();
  };

  const isOnline = realIsOnlineState && !isForcingOfflineMode;
  useEffect(() => {
    onlineManager.setOnline(isOnline);
    if (!isOnline) {
      api.setInterceptorToOfflineMode();
    }
  }, [isOnline]);

  const value = {
    isOnline,
    isForcingOfflineMode,
    isLoadingOnlineStatus,
    userHasInternet: realIsOnlineState,
    setIsForcingOfflineMode: handleSetIsForcingOfflineMode,
  };

  return <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>;
};
